import { nextTick } from 'vue'

import { useAnnouncementQueue } from '@/composables/ui/useAnnouncementQueue'
import { useMainStore } from '@/stores/main'
import { toLocaleCodes } from '@/utils/i18nLocales'
import { buildNormalizedRoute, resolveModalIntent } from '@/utils/urlModal'

// 已正規化來源網址的保留筆數上限：query 組合可無限變化（?modal=register&utm_content=N），
// 不設限在 PWA 常駐 session 下會是無界容器
const MAX_NORMALIZED_ORIGINS = 20

// 網址正規化（?modal=register → /register），規格要求以 push 寫入歷史、上一頁可退回原始推廣網址
// 規格：spec/JT-1784716920_url-modal-trigger-announcement-deferral.md REQ-9、REQ-10
//
// 為什麼不在 middleware 做：middleware 回傳的 navigateTo 是「改寫進行中的導航」，
// vue-router 會覆蓋待處理的歷史項目而非新增一筆 —— 不論有沒有帶 replace 都一樣。
// 實測 /?modal=register 正規化後按上一頁會直接回到 /，原始網址整筆消失。
// 改在 afterEach：此時原始網址已經確實寫進歷史，再 push 才會是真正的第二筆。
export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()

  // 進站當下的網址意圖，直接從 window.location 取：plugin 早於 app 掛載，
  // 不受之後任何導頁／錯誤處理影響，供 layouts/default.vue 守門用（REQ-16）。
  // 走 router 或 store 都太晚——afterEach 與頁面 onMounted 都晚於 layout 取值的時機。
  const entryUrl = new URL(window.location.href)
  const { intent: entryModalIntent } = resolveModalIntent({
    path: entryUrl.pathname,
    query: Object.fromEntries(entryUrl.searchParams.entries()),
    // $i18n 此時尚未必就緒，交由 stripLocalePrefix 的 xx-XX 退路處理
    locales: [],
  })

  // 記錄本次頁面載入中已正規化過的來源網址：不記錄的話，使用者按上一頁回到原網址
  // 會被立刻再導走一次，push 又形同 replace
  const normalizedOrigins = new Set()

  // 超過上限時淘汰最舊一筆（Set 的插入順序即為 FIFO）；被淘汰的網址若再次造訪
  // 只會多正規化一次，不影響正確性
  const rememberOrigin = (fullPath) => {
    if (normalizedOrigins.size >= MAX_NORMALIZED_ORIGINS) {
      normalizedOrigins.delete(normalizedOrigins.values().next().value)
    }
    normalizedOrigins.add(fullPath)
  }

  // 進站首次導航的 afterEach 落在 hydration 視窗內，此時 push 會被 <NuxtPage> 吞掉：
  // 網址與 router.currentRoute 都變成 /register，但 SSR 既有的頁面元件不會被換掉，
  // pages/register.vue 因此不會 mount，useUrlModalTrigger 的 onMounted 永遠不執行。
  // 改等 app:suspense:resolve（hydration 結束、isHydrating 已為 false）後再導。
  // nextTick 是等當前導航完全結束，避免 vue-router 的 navigation-during-navigation 警告。
  const pushAfterHydration = (target, origin) => {
    const push = () => {
      // 延後期間使用者可能已自行導頁，僅在仍停留於原始網址時才正規化。
      // 放棄正規化等於沒有頁面會接手開窗，必須同時解除公告延後，
      // 否則旗標會殘留整個 session，之後每一次公告都被延後掉
      if (router.currentRoute.value.fullPath !== origin) {
        nuxtApp.runWithContext(() => useAnnouncementQueue().flushDeferredAnnouncement())
        return
      }
      router.push(target)
    }

    if (!nuxtApp.isHydrating) {
      nextTick(push)
      return
    }

    nuxtApp.hooks.hookOnce('app:suspense:resolve', () => nextTick(push))
  }

  router.afterEach((to) => {
    const { $i18n } = useNuxtApp()
    const locales = toLocaleCodes($i18n?.locales)

    const { intent } = resolveModalIntent({ path: to.path, query: to.query, locales })
    if (!intent) return

    // 已登入的導頁由 middleware/url-modal.global.js 負責，此處不介入
    const { isLoggedIn } = useAuthToken()
    if (isLoggedIn.value) return

    const target = buildNormalizedRoute({ path: to.path, query: to.query, locales })
    if (!target) return
    if (normalizedOrigins.has(to.fullPath)) return
    rememberOrigin(to.fullPath)

    // 旗標在 middleware 已設，這裡補設以涵蓋 afterEach 才判定成立的路徑
    useMainStore().setUrlModalPending(intent)

    pushAfterHydration(target, to.fullPath)
  })

  return {
    provide: { entryModalIntent },
  }
})
